import { formatMinor, toMinor } from './money';
import { i18n } from './i18n';
import { ltrIn, parseQuantity } from './sales';

/**
 * Checks of the sales forms (invoice, quote, sales order, credit note, challan, sales receipt;
 * receive payment; apply credit) that mirror the API, so the user sees a line-numbered message
 * before the request instead of a generic 400/422. The server stays the authority.
 */

/** The line fields the document form validates (strings as typed). */
export interface DocumentRowInput {
  itemId: string | null;
  description: string;
  quantity: string;
  rate: string;
  taxCodeId: string | null;
}

/** A row the user has started: blank rows are dropped from the request. */
export const rowUsed = (r: Pick<DocumentRowInput, 'itemId' | 'description' | 'rate'>): boolean =>
  Boolean(r.itemId || r.description.trim() || r.rate);

/**
 * The quantity as the API wants it (`^\d+(\.\d+)?$`, above zero, at most 6 decimals): blank
 * means 1, "1,000.5" → "1000.5", ".5" → "0.5", "2." → "2". Null when it is not a quantity.
 */
export function normalizeQuantity(text: string): string | null {
  if (text.trim() === '') return '1';
  const q = parseQuantity(text);
  if (!q) return null;
  const digits = q.digits.toString().padStart(q.scale + 1, '0');
  if (q.scale === 0) return digits;
  const int = digits.slice(0, digits.length - q.scale);
  const frac = digits.slice(digits.length - q.scale).replace(/0+$/, '');
  return frac ? `${int}.${frac}` : int;
}

export interface DocumentFormInput {
  contactId: string | null;
  issueDate: string;
  /** Invoice due date, quote expiry, sales order shipment date ('' when none). */
  dueDate: string;
  amountsAre: 'exclusive' | 'inclusive' | 'out_of_scope';
  rows: DocumentRowInput[];
  /** "VAT" or "GST". */
  taxWord: string;
  /** What the due date is called on this kind ("due date", "expiration date"). */
  dueLabel?: string;
}

/** Problems that stop the document being saved, numbered as the lines on screen. */
export function documentProblems(f: DocumentFormInput): string[] {
  const problems: string[] = [];
  if (!f.contactId) problems.push(i18n.t('salesPages.document.chooseCustomer'));
  if (!f.issueDate) problems.push(i18n.t('salesPages.document.enterDate'));
  if (f.dueDate && f.issueDate && f.dueDate < f.issueDate)
    problems.push(
      i18n.t('salesPages.document.dueBeforeDate', {
        due: f.dueLabel ?? i18n.t('salesPages.document.dueDateLower'),
      }),
    );
  if (!f.rows.some(rowUsed)) problems.push(i18n.t('salesPages.document.addLine'));
  f.rows.forEach((r, i) => {
    if (!rowUsed(r)) return;
    if (normalizeQuantity(r.quantity) === null)
      problems.push(i18n.t('salesPages.document.lineQuantity', { n: i + 1 }));
    if (!r.taxCodeId && f.amountsAre !== 'out_of_scope')
      problems.push(i18n.t('salesPages.document.lineTaxCode', { n: i + 1, tax: f.taxWord }));
  });
  return problems;
}

export interface FieldErrorDetail {
  path: string;
  message: string;
}

/**
 * The API numbers lines as sent (blank rows dropped): `lines.0.quantity`, "line 1: …". Maps
 * them back to the rows on screen so the right row is highlighted and the message names the
 * line number the user sees.
 */
export function mapLineErrors(
  details: FieldErrorDetail[],
  rows: Array<Pick<DocumentRowInput, 'itemId' | 'description' | 'rate'>>,
): FieldErrorDetail[] {
  const sent: number[] = [];
  rows.forEach((r, i) => {
    if (rowUsed(r)) sent.push(i);
  });
  return details.map((d) => {
    const m = /^lines\.(\d+)(\..*)?$/.exec(d.path);
    if (!m) return d;
    const row = sent[Number(m[1])];
    if (row === undefined) return d;
    const n = row + 1;
    const message = /^line \d+:/i.test(d.message)
      ? d.message.replace(/^line \d+:/i, `line ${n}:`)
      : `line ${n}: ${d.message}`;
    return { path: `lines.${row}${m[2] ?? ''}`, message };
  });
}

export interface OpenAmount {
  id: string;
  number: string;
  /** Open balance (invoice) or credit left (credit), decimal string. */
  balance: string;
}

/**
 * Amounts typed against open items (invoices to pay, credits to use): each at most the open
 * balance, so a payment cannot over-apply an invoice or spend more credit than is left.
 */
export function overApplied(
  typed: Record<string, string>,
  items: OpenAmount[],
  exp: number,
  currency: string,
  what: 'invoice' | 'credit',
): string[] {
  const problems: string[] = [];
  for (const it of items) {
    const want = toMinor(typed[it.id] ?? '', exp) ?? 0n;
    const open = toMinor(it.balance, exp) ?? 0n;
    if (want > open)
      problems.push(
        what === 'invoice'
          ? i18n.t('salesPages.document.overApplyInvoice', {
              number: ltrIn(it.number),
              amount: ltrIn(`${currency} ${formatMinor(open, exp)}`),
            })
          : i18n.t('salesPages.document.overApplyCredit', {
              number: ltrIn(it.number),
              amount: ltrIn(`${currency} ${formatMinor(open, exp)}`),
            }),
      );
  }
  return problems;
}
